import React, { useState } from 'react'

import isValid from 'date-fns/isValid'

import { isValidDate } from 'aem-libs-react-ts/src/utils/DateUtils'

import { DateHeader, ModeDays, ModeMonths, ModeYears } from './components'
import { Mode } from './enums'

import './Datepicker.scss'

export type DisableDates = (date: Date | string) => boolean

export interface IProps {
  date?: Date
  disableDates?: DisableDates
  disableInModes?: Mode[]
  autoMove?: boolean
  enableYearsShift?: boolean
  onChange: (date: Date) => void
  onConfirmDate: (date: Date) => void
}

/**
 * Datepicker component
 *
 *
 * @param date (Date, optional) - Default date for datepicker. Set to current date if is not assigned.
 * @param disableDates (void: Date, optional) - Function which can disable specific dates
 * This function must have date on input and must return boolean value
 * @param autoMove (boolean, optional) - Enable / disable auto movement in datepicker.
 * Default value is `false`
 * @param enableYearsShift (boolean, optional) - Enable / disable years shift in datepicker years mode.
 * Default value is `false`
 * @param disableInModes (Mode[], optional) - Array with datepicker modes (day, month, years)
 * which will evaluate `disableDates` param
 * @param onChange (void: Date, required) - On change handler
 * @param onConfirmDate (void: Date, required) - On confirm date handler
 *
 */
const Datepicker = ({
  date,
  disableDates,
  disableInModes,
  autoMove,
  enableYearsShift,
  onChange,
  onConfirmDate
}: IProps) => {
  const currentDate = isValid(date) ? date : new Date()
  const disableInModesDefault = disableInModes || [Mode.Day, Mode.Month, Mode.Year]

  const defaultState = {
    mode: Mode.Day,
    date: currentDate || new Date(),
    error: false
  }

  const [state, setState] = useState(defaultState)

  const isDateDisabled = (val: Date): boolean => {
    if (disableDates) {
      return disableDates.apply(this, [val])
    }
    return false
  }

  const handleSelectDay = (selected: number) => {
    const newState = { ...state }
    newState.date?.setDate(selected)
    newState.error = isDateDisabled(newState.date)

    if (autoMove && !newState.error) {
      newState.mode = Mode.Month
    }

    onChange(newState.date)
    setState(newState)
  }

  const handleSelectMonth = (selected: number) => {
    const newState = { ...state }

    if (!isValidDate(newState.date.getFullYear(), selected, newState.date.getDate())) {
      newState.date.setDate(1)
    }

    newState.date.setMonth(selected)
    newState.error = isDateDisabled(newState.date)

    if (autoMove && !newState.error) {
      newState.mode = Mode.Year
    }

    onChange(newState.date)
    setState(newState)
  }

  const handleSelectYear = (selected: number) => {
    const newState = { ...state }

    if (!isValidDate(selected, newState.date.getMonth(), newState.date.getDate())) {
      newState.date.setDate(1)
    }

    newState.date.setFullYear(selected)
    newState.error = isDateDisabled(newState.date)

    onChange(newState.date)
    setState(newState)
  }

  const handleChangeMode = (changeMode: Mode) => {
    const newState = { ...state, mode: changeMode }
    setState(newState)
  }

  return (
    <div className='cs-rdatepicker'>
      <DateHeader
        mode={state.mode}
        dateDisabled={state.error}
        selectedDate={state.date}
        onChangeMode={handleChangeMode}
      />
      {state.mode === Mode.Day && (
        <ModeDays
          disableDates={disableDates}
          disableInModes={disableInModesDefault}
          selectedDate={state.date}
          onSelectDay={handleSelectDay}
        />
      )}
      {state.mode === Mode.Month && (
        <ModeMonths
          disableDates={disableDates}
          disableInModes={disableInModesDefault}
          selectedDate={state.date}
          onSelectMonth={handleSelectMonth}
        />
      )}
      {state.mode === Mode.Year && (
        <ModeYears
          disableDates={disableDates}
          disableInModes={disableInModesDefault}
          enableYearsShift={enableYearsShift}
          selectedDate={state.date}
          onSelectYear={handleSelectYear}
        />
      )}
      <div className='cs-rdatepicker__confirm'>
        <button type='button' className='cs-rdatepicker__confirm-button' onClick={() => onConfirmDate(state.date)}>
          OK
        </button>
      </div>
    </div>
  )
}

export { Datepicker }
